(function () {
  const SELECTORS = {
    holder: '[data-cart-complementary-products]',
    scroll: '[data-cart-complementary-scroll]',
    prev: '[data-cart-complementary-prev]',
    next: '[data-cart-complementary-next]',
    card: ':scope > .product-item',
    sectionTemplate: '[data-api-related-template]',
  };

  const CLASSES = {
    hidden: 'is-hidden',
    loading: 'is-loading',
  };

  const SCROLL_THRESHOLD = 4;

  class CartComplementary extends HTMLElement {
    constructor() {
      super();
      this.onCartChange = this.onCartChange.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onScrollEnd = this.onScrollEnd.bind(this);
      this.requestToken = 0;
    }

    connectedCallback() {
      this.holder = this.querySelector(SELECTORS.holder);
      this.scrollEl = this.querySelector(SELECTORS.scroll);
      this.prevBtn = this.querySelector(SELECTORS.prev);
      this.nextBtn = this.querySelector(SELECTORS.next);

      if (!this.holder || !this.scrollEl || !this.prevBtn || !this.nextBtn) return;

      this.prevBtn.addEventListener('click', this.onPrevClick);
      this.nextBtn.addEventListener('click', this.onNextClick);
      this.scrollEl.addEventListener('scroll', this.onScroll, {passive: true});
      this.scrollEl.addEventListener('scrollend', this.onScrollEnd);
      document.addEventListener('theme:cart:change', this.onCartChange);

      this.resizeObserver = new ResizeObserver(() => this.scheduleArrowStateUpdate());
      this.resizeObserver.observe(this.scrollEl);
      this.resizeObserver.observe(this.holder);

      this.refresh();
    }

    disconnectedCallback() {
      this.prevBtn?.removeEventListener('click', this.onPrevClick);
      this.nextBtn?.removeEventListener('click', this.onNextClick);
      this.scrollEl?.removeEventListener('scroll', this.onScroll);
      this.scrollEl?.removeEventListener('scrollend', this.onScrollEnd);
      document.removeEventListener('theme:cart:change', this.onCartChange);
      this.resizeObserver?.disconnect();
    }

    onCartChange() {
      this.refresh();
    }

    async refresh() {
      this.requestToken += 1;
      const token = this.requestToken;
      this.classList.add(CLASSES.loading);

      try {
        const cart = await this.fetchJSON(`${window.theme.routes.cart_url}.js`, Number(this.dataset.requestTimeout) || 3500);
        if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
          this.hide();
          return;
        }

        const limitCartProducts = Number(this.dataset.limitCartProducts) || 3;
        const maxProducts = Number(this.dataset.maxProducts) || 12;
        const intent = this.dataset.intent || 'complementary';
        const cartItems = cart.items.slice(0, limitCartProducts);
        const cartProductIds = new Set(cart.items.map((item) => item.product_id));

        const recommendationTasks = cartItems.map(async (item) => {
          const timeoutMs = Number(this.dataset.requestTimeout) || 3500;
          try {
            const [jsonResponse, htmlResponse] = await Promise.all([
              this.fetchJSON(
                `${window.theme.routes.root}recommendations/products.json?product_id=${item.product_id}&intent=${intent}&limit=10`,
                timeoutMs
              ),
              this.fetchText(
                `${window.theme.routes.root}recommendations/products?section_id=api-product-recommendation&product_id=${item.product_id}&intent=${intent}&limit=10`,
                timeoutMs
              ),
            ]);

            const htmlByHandle = this.extractProductCardsByHandle(htmlResponse);
            return {
              ok: true,
              products: Array.isArray(jsonResponse?.products) ? jsonResponse.products : [],
              htmlByHandle,
            };
          } catch (error) {
            return {ok: false, products: [], htmlByHandle: new Map()};
          }
        });

        const recommendationResults = await Promise.all(recommendationTasks);

        if (token !== this.requestToken) return;

        const mergedProducts = [];
        const seenProductIds = new Set();
        const cardHTMLByHandle = new Map();
        let hasAnySuccess = false;

        recommendationResults.forEach((result) => {
          if (result.ok) hasAnySuccess = true;
          if (result.htmlByHandle?.size) {
            result.htmlByHandle.forEach((html, handle) => {
              if (!cardHTMLByHandle.has(handle)) {
                cardHTMLByHandle.set(handle, html);
              }
            });
          }
          result.products.forEach((product) => {
            if (!product || typeof product.id !== 'number') return;
            if (!product.available) return;
            if (cartProductIds.has(product.id)) return;
            if (seenProductIds.has(product.id)) return;

            seenProductIds.add(product.id);
            mergedProducts.push(product);
          });
        });

        if (!hasAnySuccess || mergedProducts.length === 0) {
          this.hide();
          return;
        }

        const handles = mergedProducts
          .slice(0, maxProducts)
          .map((product) => product.handle)
          .filter(Boolean);

        if (handles.length === 0) {
          this.hide();
          return;
        }

        const fragments = handles
          .map((handle) => cardHTMLByHandle.get(handle))
          .filter(Boolean);

        if (!fragments.length) {
          this.hide();
          return;
        }

        this.holder.innerHTML = '';
        fragments.forEach((html) => {
          const template = document.createElement('template');
          template.innerHTML = html.trim();
          const node = template.content.firstElementChild;
          if (node) {
            this.holder.appendChild(node);
          }
        });
        this.classList.remove(CLASSES.hidden);
        this.classList.remove(CLASSES.loading);

        this.scrollEl.scrollLeft = 0;
        this.scheduleArrowStateUpdate();
      } catch (error) {
        if (token !== this.requestToken) return;
        this.hide();
      }
    }

    hide() {
      this.holder.innerHTML = '';
      this.classList.add(CLASSES.hidden);
      this.classList.remove(CLASSES.loading);
      this.updateArrowState();
    }

    getCards() {
      return Array.from(this.holder.querySelectorAll(SELECTORS.card));
    }

    getMaxScrollLeft() {
      const {scrollWidth, clientWidth} = this.scrollEl;
      return Math.max(0, scrollWidth - clientWidth);
    }

    getScrollStep() {
      const cards = this.getCards();
      if (cards.length < 2) {
        const card = cards[0];
        if (!card) return 0;
        const styles = window.getComputedStyle(this.holder);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return card.offsetWidth + gap;
      }

      return Math.max(1, cards[1].offsetLeft - cards[0].offsetLeft);
    }

    clampScroll() {
      const maxScroll = this.getMaxScrollLeft();
      if (this.scrollEl.scrollLeft > maxScroll + SCROLL_THRESHOLD) {
        this.scrollEl.scrollLeft = maxScroll;
      }
    }

    onPrevClick() {
      if (this.prevBtn.disabled) return;
      const step = this.getScrollStep();
      if (!step) return;

      const target = Math.max(0, this.scrollEl.scrollLeft - step);
      this.scrollEl.scrollTo({left: target, behavior: 'smooth'});
    }

    onNextClick() {
      if (this.nextBtn.disabled) return;
      const step = this.getScrollStep();
      if (!step) return;

      const maxScroll = this.getMaxScrollLeft();
      const target = Math.min(this.scrollEl.scrollLeft + step, maxScroll);
      this.scrollEl.scrollTo({left: target, behavior: 'smooth'});
    }

    onScroll() {
      this.updateArrowState();
    }

    onScrollEnd() {
      this.clampScroll();
      this.updateArrowState();
    }

    scheduleArrowStateUpdate() {
      requestAnimationFrame(() => {
        this.clampScroll();
        this.updateArrowState();
        requestAnimationFrame(() => {
          this.clampScroll();
          this.updateArrowState();
        });
      });

      this.holder.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener(
          'load',
          () => {
            this.clampScroll();
            this.updateArrowState();
          },
          {once: true}
        );
      });
    }

    updateArrowState() {
      if (!this.scrollEl || !this.prevBtn || !this.nextBtn) return;

      const cards = this.getCards();
      if (!cards.length) {
        this.prevBtn.disabled = true;
        this.nextBtn.disabled = true;
        return;
      }

      const maxScroll = this.getMaxScrollLeft();
      const scrollLeft = this.scrollEl.scrollLeft;
      const canScroll = maxScroll > SCROLL_THRESHOLD;

      this.prevBtn.disabled = !canScroll || scrollLeft <= SCROLL_THRESHOLD;
      this.nextBtn.disabled = !canScroll || scrollLeft >= maxScroll - SCROLL_THRESHOLD;
    }

    fetchJSON(url, timeoutMs) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

      return fetch(url, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .finally(() => {
          window.clearTimeout(timeout);
        });
    }

    fetchText(url, timeoutMs) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

      return fetch(url, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .finally(() => {
          window.clearTimeout(timeout);
        });
    }

    extractProductCardsByHandle(htmlText) {
      const result = new Map();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = htmlText;

      const template = wrapper.querySelector(SELECTORS.sectionTemplate);
      const source = template ? template.content : wrapper;
      const cards = source.querySelectorAll('.product-item');

      cards.forEach((card) => {
        const link =
          card.querySelector('[data-product-link]') ||
          card.querySelector('a[href*="/products/"]');
        const href = link?.getAttribute('href') || '';
        const match = href.match(/\/products\/([^/?#]+)/);
        const handle = match ? decodeURIComponent(match[1]) : '';

        if (handle && !result.has(handle)) {
          result.set(handle, card.outerHTML);
        }
      });

      return result;
    }
  }

  if (!customElements.get('cart-complementary')) {
    customElements.define('cart-complementary', CartComplementary);
  }
})();
